import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import { imgList } from "@constants/imgList";
import {
  MetricDetailLayout,
  MetricFilterControls,
  SummaryBlock,
  useDeviceReport,
  timeColumn,
  numericColumn,
  plainColumn,
} from "@components/MetricDetail";
import type { FetchReport } from "@components/MetricDetail";
import Excel from "@components/Excel";
import Table from "@components/Table";
import ChartMetricLine from "@components/Charts/ChartMetricLine";
import ChartPlaceholder from "@components/Charts/ChartPlaceholder";
import { displayImage } from "@utils/image";
import { localAnchorStr } from "@utils/date";
import { apiGetReportWindow } from "@apis/reportWindow";
import { toFixedOrDash } from "@utils/string";
import { runReportAggregation } from "@utils/reportTableWorkerClient";
import {
  computeStats,
  downsampleAvg,
  recommendedResolution,
} from "@utils/temperatureHistory";

// RSSI is the only numeric series — averaged per resolution window; wifi/
// cloudStatus are on/off states carried through downsampleAvg from each window.
const IOT_NUMERIC_KEYS = ["rssi"];

// Inline icons — same set the temperature/power pages use for the download
// button and the chart/table section headings.
const DownloadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const ChartLineIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FEBC11"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19l16 0" />
    <path d="M4 15l4 -6l4 2l4 -5l4 4" />
  </svg>
);
const TableIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FEBC11"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14z" />
    <path d="M3 10h18" />
    <path d="M10 3v18" />
  </svg>
);

export default function IOTDetail() {
  const [dataTemp, setDataTemp] = useState<any>({});
  // Display resolution (minutes) for the chart + table; auto-snapped to the
  // recommended value per range, overridable via the chips.
  const [resolution, setResolution] = useState(1);
  const getReportIot: FetchReport = async (filterDeviceDetail, language, id) => {
    const res = await apiGetReportWindow(
      (anchor: string) =>
        `${
          process.env.REACT_APP_API_DOMAIN
        }/monitoring-api/device-report/${id}/iot-status?summarize=false&dateTime=${anchor}&dateTimeUnit=${filterDeviceDetail.unit}`,
      localAnchorStr(filterDeviceDetail.date, filterDeviceDetail.period),
      filterDeviceDetail.unit,
      language
    );
    if (res.status && res.code === 200) {
      const dateTimeStr = `${dayjs(filterDeviceDetail.date).format(
        "YYYY-MM-DD"
      )} ${
        filterDeviceDetail.period.toString().length < 2
          ? `0${filterDeviceDetail.period}:00:00`
          : `${filterDeviceDetail.period}:00:00`
      }`;
      const { wifiResults, rssiResults } = await runReportAggregation("iot", {
        rawResult: res?.data?.data?.result ?? [],
        dateTimeStr,
        unit: filterDeviceDetail.unit,
      });
      setDataTemp({ wifiResults, rssiResults });
    }
  };
  const {
    t,
    navigate,
    id,
    deviceDetail,
    filterDeviceDetail,
    plan,
    listUnit,
    listPeriod,
    dataReportDetail,
    renderSound,
  } = useDeviceReport(getReportIot);
  // Wi-Fi network the device is joined to (same source the device-detail iot
  // section reads) — shown beside the summary title for context.
  const ssid =
    (dataReportDetail?.ssid ?? "") === "" || dataReportDetail?.ssid === null
      ? ""
      : dataReportDetail?.ssid;

  // wifiResults + rssiResults are parallel arrays over the same minute grid —
  // merge them into one row set so the chart/table/Excel share a single source.
  const mergedRows: any[] = useMemo(() => {
    const wifi: any[] = dataTemp?.wifiResults ?? [];
    const rssi: any[] = dataTemp?.rssiResults ?? [];
    return wifi.map((e: any, i: number) => ({
      time: e?.time ?? "",
      wifi: e?.wifi,
      cloudStatus: e?.cloudStatus,
      rssi: rssi?.[i]?.rssi ?? null,
    }));
  }, [dataTemp]);

  // At-a-glance summary — RSSI avg/weakest via computeStats (null-filtered per
  // the null-vs-0 rule), plus Wi-Fi disconnect count and online uptime %. Stats
  // run on the RAW rows (resolution-independent).
  const rssiStat = useMemo(
    () =>
      computeStats(
        mergedRows
          .map((r: any) => ({
            time: r?.time ?? "",
            value:
              r?.rssi === null || r?.rssi === undefined || r?.rssi === ""
                ? NaN
                : Number(r?.rssi),
          }))
          .filter((r: any) => Number.isFinite(r.value))
      ),
    [mergedRows]
  );
  const summary = useMemo(() => {
    // data-carrying rows only — grid gap rows (all fields blank) must not count
    const dataRows = mergedRows.filter(
      (r: any) =>
        r?.wifi !== undefined || r?.cloudStatus !== undefined || r?.rssi !== null
    );
    if (dataRows.length === 0) return null;
    let wifiDrops = 0;
    let onlineCount = 0;
    let statusCount = 0;
    mergedRows.forEach((r: any, i: number) => {
      const prev = mergedRows[i - 1];
      if (prev && prev.wifi === true && r.wifi === false) wifiDrops += 1;
      if (r.cloudStatus === true || r.cloudStatus === false) {
        statusCount += 1;
        if (r.cloudStatus === true) onlineCount += 1;
      }
    });
    const onlinePct =
      statusCount > 0 ? Math.round((onlineCount / statusCount) * 100) : null;
    const first = dataRows[0];
    const last = dataRows[dataRows.length - 1];
    const diffMin = Math.max(
      0,
      dayjs(last?.time).diff(dayjs(first?.time), "minute")
    );
    return {
      wifiDrops,
      onlinePct,
      diffMin,
      from: first?.time ?? "",
      to: last?.time ?? "",
    };
  }, [mergedRows]);
  const durationLabel = summary
    ? summary.diffMin >= 60
      ? `${Math.floor(summary.diffMin / 60)} ${t("detail.hour")} ${
          summary.diffMin % 60
        } ${t("detail.minunit")}`
      : `${summary.diffMin} ${t("detail.minunit")}`
    : "";
  const periodLabel = t(
    `detail.period${filterDeviceDetail.unit}` as any
  ) as string;

  // Auto-snap resolution to the recommended value when the range changes.
  useEffect(() => {
    setResolution(recommendedResolution(filterDeviceDetail.unit));
  }, [filterDeviceDetail.unit]);

  // Chart + table + Excel all render these downsampled rows (RSSI averaged per
  // window; wifi/cloudStatus carried from each window's first sample).
  const displayRows: any[] = useMemo(
    () => downsampleAvg(mergedRows, resolution, IOT_NUMERIC_KEYS),
    [mergedRows, resolution]
  );

  const RESOLUTIONS = [1, 5, 10, 30, 60];
  const columns = [
    timeColumn(t),
    plainColumn(t, "detail.wifi", "wifi"),
    plainColumn(t, "detail.devicestatus", "cloudStatus"),
    numericColumn(t, "detail.rssi", "rssi"),
  ];

  // One row → display-label mapping shared by the table AND the Excel sheet.
  const wifiLabel = (e: any) =>
    (e?.wifi ?? "-") === "-"
      ? "-"
      : e?.wifi
      ? t("detail.connect")
      : t("detail.unable");
  const cloudLabel = (e: any) =>
    (e?.cloudStatus ?? "-") === "-"
      ? "-"
      : e?.cloudStatus
      ? t("detail.online")
      : t("detail.offline");

  // ── Excel: 2 sheets (summary / readings), mirroring the power page ──
  const F = t("detail.xlsfield");
  const V = t("detail.xlsvalue");
  const kv = (label: string, value: any) => ({ [F]: label, [V]: value });
  const excelInfoData = summary
    ? [
        kv(t("detail.name"), deviceDetail?.deviceInfo?.name ?? "-"),
        kv(
          t("detail.mac"),
          deviceDetail?.controlInfo?.macAddress ??
            deviceDetail?.deviceInfo?.macAddress ??
            "-"
        ),
        kv(t("monitoring.location"), deviceDetail?.name ?? "-"),
        kv(t("detail.iothead"), ssid || "-"),
        kv(t("detail.reporttype"), periodLabel),
        kv(
          t("detail.dataperiod"),
          `${dayjs(summary.from).format("DD/MM/YYYY HH:mm")} - ${dayjs(
            summary.to
          ).format("DD/MM/YYYY HH:mm")} (${durationLabel})`
        ),
        kv(
          `${t("detail.rssiname")} (${t("detail.average")})`,
          rssiStat.avg === null ? "-" : `${toFixedOrDash(rssiStat.avg)} dBm`
        ),
        kv(
          `${t("detail.rssiname")} (${t("detail.lowest")})`,
          rssiStat.min === null ? "-" : `${toFixedOrDash(rssiStat.min)} dBm`
        ),
        kv(
          t("detail.wifidrop"),
          `${summary.wifiDrops} ${t("detail.times")}`
        ),
        kv(
          t("detail.online"),
          summary.onlinePct === null ? "-" : `${summary.onlinePct}%`
        ),
        kv(t("detail.printedat"), dayjs().format("DD/MM/YYYY HH:mm")),
      ]
    : [];
  const excelReadingHeader = [
    t("detail.time"),
    t("detail.wifi"),
    t("detail.devicestatus"),
    t("detail.rssi"),
  ];
  const excelReadingData = displayRows.map((e: any) => ({
    Time: dayjs(e?.time ?? "").format("DD/MM/YYYY HH:mm"),
    wifi: wifiLabel(e),
    "Device Status": cloudLabel(e),
    rssi: toFixedOrDash(e?.rssi),
  }));
  const excelSheets = [
    { name: t("detail.xlsinfo"), data: excelInfoData },
    {
      name: t("detail.iotdetail"),
      header: excelReadingHeader,
      data: excelReadingData,
    },
  ];

  return (
    <MetricDetailLayout
      title={t("detail.iotdetail")}
      metricLabel={t("detail.iot")}
      devicesLabel={t("devices.head")}
      onBack={() => navigate(`/device-detail/${id}`)}
      onBreadcrumbDevices={() => navigate(`/devices`)}
      deviceName={deviceDetail?.deviceInfo?.name ?? ""}
      image={
        (deviceDetail?.deviceInfo?.image ?? "")
          ? displayImage(deviceDetail?.deviceInfo?.image ?? "")
          : imgList.ex
      }
      locationName={deviceDetail?.name ?? ""}
      soundSlot={renderSound(
        deviceDetail?.controlInfo?.macAddress ?? "",
        deviceDetail?.controlInfo?.soundAlarm ?? false,
        deviceDetail?.controlInfo?.control?.alarm ?? false
      )}
      headerActions={
        <Excel
          fileName={`${id}-${t("detail.iothead")}-${dayjs().format(
            "YYYYMMDDHHmmss"
          )}`}
          sheets={excelSheets as any}
          button={
            <div
              className="size-14 weight-600 color-black pointer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid #e2e8f0",
                padding: "6px 14px",
                borderRadius: 8,
                whiteSpace: "nowrap",
              }}
            >
              <DownloadIcon />
              {t("detail.downloadexcel")}
            </div>
          }
        />
      }
      breadcrumbActions={
        <MetricFilterControls
          filterDeviceDetail={filterDeviceDetail}
          plan={plan}
          listUnit={listUnit}
          listPeriod={listPeriod}
        />
      }
      headlineExtra={
        summary ? (
          // Mirrors the temperature/power summary chrome (SummaryBlock): signal
          // + connectivity tiles, then the report-type / recording-period line.
          <SummaryBlock>
            <div className="sum__head">
              {t("detail.iotsummary")}
              {ssid ? (
                <span
                  style={{
                    marginLeft: 12,
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#64748b",
                  }}
                >
                  · {t("detail.iothead")}: {ssid}
                </span>
              ) : null}
            </div>
            <div className="sum__row">
              <div className="sum__center">
                <div className="sum__tiles">
                  <div className="sum__tile">
                    <div className="sum__tile-label">
                      {t("detail.rssiname")} ({t("detail.average")})
                    </div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {rssiStat.avg === null
                        ? "-"
                        : toFixedOrDash(rssiStat.avg)}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>dBm</span>
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">
                      {t("detail.rssiname")} ({t("detail.lowest")})
                    </div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {rssiStat.min === null
                        ? "-"
                        : toFixedOrDash(rssiStat.min)}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>dBm</span>
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">
                      {t("detail.wifidrop")}
                    </div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {summary.wifiDrops}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {t("detail.times")}
                      </span>
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">{t("detail.online")}</div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {summary.onlinePct === null ? "-" : summary.onlinePct}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>%</span>
                    </div>
                  </div>
                </div>
                <div className="sum__duration">
                  {t("detail.reporttype")}: {periodLabel}
                  {"  ·  "}
                  {t("detail.dataperiod")}: {durationLabel}
                </div>
              </div>
            </div>
          </SummaryBlock>
        ) : null
      }
      filterBar={
        <div className="detail__filter">
          {/* chart section heading + resolution chips (drive chart + table +
              Excel together) — matches the temperature/power pages */}
          <div
            className="detail__head-2"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <ChartLineIcon />
            <span className="color-black size-20 font-sukhumvitSet-bold">
              {t("detail.iotchart")}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginLeft: "auto",
            }}
          >
            {RESOLUTIONS.map((r) => (
              <div
                key={r}
                className="pointer"
                onClick={() => setResolution(r)}
                style={{
                  padding: "4px 14px",
                  borderRadius: 16,
                  border: `1px solid ${
                    resolution === r ? "#FEBC11" : "#e2e8f0"
                  }`,
                  background: resolution === r ? "#fffbeb" : "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  color: resolution === r ? "#b45309" : "#64748b",
                }}
              >
                {r >= 60
                  ? `${r / 60} ${t("detail.hour")}`
                  : `${r} ${t("detail.minunit")}`}
              </div>
            ))}
          </div>
        </div>
      }
      chart={
        displayRows.length > 0 ? (
          <ChartMetricLine
            data={displayRows}
            dataKey="rssi"
            name={t("detail.rssi")}
            color="#8b5cf6"
            unitSuffix=" dBm"
            height={260}
          />
        ) : (
          <ChartPlaceholder
            loading={!Array.isArray(dataTemp?.wifiResults)}
            loadingText={t("detail.loadingdata")}
            emptyText={t("detail.nodatarange")}
          />
        )
      }
      table={
        <div className="detail__table">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: "8px 0 16px",
            }}
          >
            <TableIcon />
            <span className="color-black size-20 font-sukhumvitSet-bold">
              {t("detail.iottable")}
            </span>
          </div>
          <Table
            pagination={{
              locale: { items_per_page: t("detail.perpage") },
              showLessItems: true,
              showTotal: (total: number, range: [number, number]) =>
                `${t("detail.showing")} ${range[0].toLocaleString(
                  "en-US"
                )}-${range[1].toLocaleString("en-US")} ${t(
                  "detail.oftotal"
                )} ${total.toLocaleString("en-US")} ${t("detail.rowunit")}`,
            }}
            showSorterTooltip={false}
            columns={columns}
            dataSource={displayRows.map((e: any, index: number) => ({
              key: index,
              time: dayjs(e?.time ?? "").format("DD/MM/YYYY HH:mm"),
              wifi: wifiLabel(e),
              cloudStatus: cloudLabel(e),
              rssi: toFixedOrDash(e?.rssi),
            }))}
          />
        </div>
      }
    />
  );
}
